import { useState } from "react";
import axios from "axios";
import { Star, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { formatApiErrorDetail } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const labelCls = "mb-2 block text-[10px] font-medium uppercase tracking-[0.28em] text-[#8C713B]";
const inputCls =
  "w-full border-b border-[#2A2421] bg-transparent py-2 text-sm text-[#F5F2EB] outline-none transition-colors focus:border-[#C5A059]";

const empty = { name: "", city: "", rating: 5, quote: "" };

const ReviewForm = ({ onDone }) => {
  const [form, setForm] = useState(empty);
  const [hover, setHover] = useState(0);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/reviews`, { ...form, city: form.city || null });
      toast.success("Thank you! Your review will appear once our team approves it.");
      setForm(empty);
      onDone?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit your review.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form data-testid="review-form" onSubmit={submit} className="border border-[#2A2421] bg-[#161311] p-8 sm:p-10">
      <h3 className="font-display text-2xl text-[#F5F2EB]">Share Your Experience</h3>
      <p className="mt-2 text-xs text-[#78716C]">Reviews are published after a quick check by our team.</p>
      <div className="mt-6 grid gap-6 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="rv-name">Name</label>
          <input id="rv-name" data-testid="review-name-input" required minLength={2} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Your name" />
        </div>
        <div>
          <label className={labelCls} htmlFor="rv-city">City</label>
          <input id="rv-city" data-testid="review-city-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} placeholder="Where are you from?" />
        </div>
      </div>
      <div className="mt-6">
        <span className={labelCls}>Rating</span>
        <div className="flex gap-2" data-testid="review-rating-picker" onMouseLeave={() => setHover(0)}>
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              data-testid={`review-star-${n}`}
              aria-label={`${n} star${n > 1 ? "s" : ""}`}
              onMouseEnter={() => setHover(n)}
              onClick={() => setForm({ ...form, rating: n })}
              className="transition-transform hover:scale-110"
            >
              <Star size={22} className="text-[#C5A059]" fill={n <= (hover || form.rating) ? "#C5A059" : "transparent"} />
            </button>
          ))}
        </div>
      </div>
      <div className="mt-6">
        <label className={labelCls} htmlFor="rv-quote">Your Review</label>
        <textarea id="rv-quote" data-testid="review-quote-input" required minLength={10} maxLength={600} rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} className={`${inputCls} resize-none`} placeholder="What made your stay memorable?" />
      </div>
      <button
        data-testid="review-submit-button"
        type="submit"
        disabled={sending}
        className="mt-8 flex items-center justify-center gap-2 border border-[#C5A059] bg-[#C5A059] px-9 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#0D0B0A] transition-all duration-300 hover:bg-transparent hover:text-[#C5A059] disabled:opacity-60"
      >
        {sending && <Loader2 size={15} className="animate-spin" />}
        {sending ? "Submitting..." : "Submit Review"}
      </button>
    </form>
  );
};

export default ReviewForm;
